import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupAccount, setToken, SetupData } from '../api';

// ─────────────────────────────────────────────
// Step definitions
// ─────────────────────────────────────────────

const TOTAL_STEPS = 3;

// ─────────────────────────────────────────────
// Step indicator
// ─────────────────────────────────────────────

function StepIndicator({
  current,
  total,
}: {
  current: number;
  total: number;
}): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        marginBottom: '28px',
      }}
    >
      {Array.from({ length: total }, (_, i) => {
        const stepNum = i + 1;
        const isDone = stepNum < current;
        const isActive = stepNum === current;
        return (
          <React.Fragment key={stepNum}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.78rem',
                fontWeight: 700,
                flexShrink: 0,
                backgroundColor: isDone
                  ? 'var(--accent-green)'
                  : isActive
                  ? 'var(--accent-blue)'
                  : 'var(--bg-hover)',
                color: isDone || isActive ? '#fff' : 'var(--text-muted)',
                border: isActive
                  ? '2px solid var(--accent-blue)'
                  : '2px solid transparent',
                transition: 'all 0.2s ease',
              }}
            >
              {isDone ? '✓' : stepNum}
            </div>
            {stepNum < total && (
              <div
                style={{
                  flex: 1,
                  height: '2px',
                  backgroundColor: isDone
                    ? 'var(--accent-green)'
                    : 'var(--border)',
                  transition: 'background-color 0.2s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// Setup wizard component
// ─────────────────────────────────────────────

const Setup: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [apiKey, setApiKey] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [capitalPassword, setCapitalPassword] = useState('');
  const [accountType] = useState<'LIVE'>('LIVE');

  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');

  // ── Validation helpers ──

  const validateStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!username.trim()) errs.username = 'Username is required.';
    else if (username.trim().length < 3)
      errs.username = 'Username must be at least 3 characters.';
    if (!password) errs.password = 'Password is required.';
    else if (password.length < 6)
      errs.password = 'Password must be at least 6 characters.';
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password.';
    else if (password !== confirmPassword)
      errs.confirmPassword = 'Passwords do not match.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!apiKey.trim()) errs.apiKey = 'API key is required.';
    if (!identifier.trim()) errs.identifier = 'Identifier is required.';
    if (!capitalPassword) errs.capitalPassword = 'Capital.com password is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setLoading(true);

    const payload: SetupData = {
      username: username.trim(),
      password,
      capital_api_key: apiKey.trim(),
      capital_identifier: identifier.trim(),
      capital_password: capitalPassword,
      account_type: accountType,
      ...(telegramToken.trim() ? { telegram_token: telegramToken.trim() } : {}),
      ...(telegramChatId.trim() ? { telegram_chat_id: telegramChatId.trim() } : {}),
    };

    try {
      const res = await setupAccount(payload);
      setToken(res.data.access_token);
      navigate('/', { replace: true });
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Setup failed. Please check your credentials and try again.';
      setSubmitError(
        typeof detail === 'string' ? detail : JSON.stringify(detail)
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Field error helper ──
  const fieldError = (key: string) =>
    errors[key] ? (
      <span className="form-error">{errors[key]}</span>
    ) : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '500px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>⚡</div>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Setup Wizard
          </h1>
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              marginTop: '6px',
            }}
          >
            Configure your Capital.com trading bot
          </p>
        </div>

        <div className="card" style={{ padding: '32px' }}>
          <StepIndicator current={step} total={TOTAL_STEPS} />

          {/* ── Step 1: Account credentials ── */}
          {step === 1 && (
            <form onSubmit={handleNext} noValidate>
              <h2
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  color: 'var(--text-primary)',
                }}
              >
                Step 1 — Create your account
              </h2>

              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="su-username">
                    Username
                  </label>
                  <input
                    id="su-username"
                    type="text"
                    className="form-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a username"
                    autoFocus
                    autoComplete="username"
                  />
                  {fieldError('username')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="su-password">
                    Password
                  </label>
                  <input
                    id="su-password"
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                  />
                  {fieldError('password')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="su-confirm">
                    Confirm password
                  </label>
                  <input
                    id="su-confirm"
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                  {fieldError('confirmPassword')}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-full"
                  style={{ marginTop: '8px' }}
                >
                  Next →
                </button>
              </div>
            </form>
          )}

          {/* ── Step 2: Capital.com API ── */}
          {step === 2 && (
            <form onSubmit={handleNext} noValidate>
              <h2
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                Step 2 — Capital.com API
              </h2>
              <p
                style={{
                  fontSize: '0.82rem',
                  color: 'var(--text-muted)',
                  marginBottom: '20px',
                }}
              >
                Find your API key in My Account → API integrations on
                Capital.com.
              </p>

              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="su-apikey">
                    API Key
                  </label>
                  <input
                    id="su-apikey"
                    type="text"
                    className="form-input"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {fieldError('apiKey')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="su-identifier">
                    Account Identifier (email / login)
                  </label>
                  <input
                    id="su-identifier"
                    type="text"
                    className="form-input"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                  {fieldError('identifier')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="su-cappass">
                    Capital.com Password
                  </label>
                  <input
                    id="su-cappass"
                    type="password"
                    className="form-input"
                    value={capitalPassword}
                    onChange={(e) => setCapitalPassword(e.target.value)}
                    placeholder="Your Capital.com login password"
                    autoComplete="current-password"
                  />
                  {fieldError('capitalPassword')}
                </div>

                <div className="form-group">
                  <label className="form-label">Account Type</label>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--accent-orange)',
                      backgroundColor: 'rgba(249,115,22,0.08)',
                      fontSize: '0.875rem',
                      color: 'var(--accent-orange)',
                      fontWeight: 600,
                    }}
                  >
                    🔴 LIVE account
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '0.72rem',
                        fontWeight: 400,
                        color: 'var(--text-muted)',
                      }}
                    >
                      Real money trading
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '8px',
                  }}
                >
                  <button
                    type="button"
                    className="btn w-full"
                    onClick={handleBack}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* ── Step 3: Telegram (optional) ── */}
          {step === 3 && (
            <form onSubmit={handleSubmit} noValidate>
              <h2
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                Step 3 — Telegram Notifications
                <span
                  style={{
                    marginLeft: '8px',
                    fontSize: '0.7rem',
                    fontWeight: 400,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  Optional
                </span>
              </h2>

              {/* Instructions box */}
              <div
                className="alert-strip info"
                style={{ marginBottom: '20px', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', fontSize: '0.8rem' }}
              >
                <div>
                  <strong>How to create a bot:</strong>
                </div>
                <ol
                  style={{
                    paddingLeft: '18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <li>
                    Open Telegram and start a chat with{' '}
                    <strong>@BotFather</strong>
                  </li>
                  <li>
                    Send <code>/newbot</code> and follow the prompts to get your
                    bot token
                  </li>
                  <li>
                    Get your chat ID by messaging{' '}
                    <strong>@userinfobot</strong> — it replies with your numeric
                    ID
                  </li>
                </ol>
              </div>

              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="su-tg-token">
                    Bot Token
                  </label>
                  <input
                    id="su-tg-token"
                    type="text"
                    className="form-input"
                    value={telegramToken}
                    onChange={(e) => setTelegramToken(e.target.value)}
                    placeholder="123456789:ABCdef..."
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="su-tg-chat">
                    Chat ID
                  </label>
                  <input
                    id="su-tg-chat"
                    type="text"
                    className="form-input"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="123456789"
                    autoComplete="off"
                  />
                </div>

                {submitError && (
                  <div className="alert-strip error" style={{ fontSize: '0.82rem' }}>
                    ⚠️ {submitError}
                  </div>
                )}

                <div
                  style={{ display: 'flex', gap: '10px', marginTop: '8px' }}
                >
                  <button
                    type="button"
                    className="btn w-full"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner" style={{ width: 16, height: 16 }} />
                        Saving…
                      </>
                    ) : (
                      '🚀 Save & Launch'
                    )}
                  </button>
                </div>

                <p
                  style={{
                    textAlign: 'center',
                    fontSize: '0.78rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  You can skip Telegram and configure it later in Settings.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Setup;
